/**
 * Lead-related strings.
 */
import React from 'react';
import Icons from '../ui/IconButton/Icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import RoutedIconButton from "../ui/Buttons/RoutedIconButton";

export default {
    d_cost: 'Cost',
    h_cost: 'The cost of a lead is calculated based on the campaigns that generated the lead. The more campaigns that were used to generated the lead, the higher the cost. As more leads are added to the system, the cost per lead will go down, as the cost of the campaign is being spread out. Unless, of course, you are paying a per-lead fee for each of your leads.',

    d_open: 'Open',
    h_open: 'The lead is still active: it has neither been won, lost or refused.',

    d_awaitingInfo: 'Awaiting Info',
    h_awaitingInfo: 'The firm is awaiting information for this PNC. This can happen when we know there is a referral coming, but they have not contacted us yet.',

    d_contactInfoReceived: 'Contact Info Received',
    h_contactInfoReceived: 'The firm has received at least some information by which you can contact the PNC. This should be an email or phone number.',

    d_contactMade: 'Contact Made',
    h_contactMade: <div>
        <p>A member of the firm has personally made contact with the client.</p>
        <p>This can be through phone, email or text.</p>
    </div>,

    d_appointmentMade: 'Appointment Made',
    h_appointmentMade: 'The PNC has an appointment scheduled with your firm.',

    d_initialConsult: 'Initial Consult Held',
    h_initialConsult: 'The PNC has had an initial consultation with a member of the legal team.',

    d_retainerSigned: 'Retainer Signed',
    h_retainerSigned: <div>
        <p>The PNC has signed a retainer agreement, but has not yet funded the retainer.</p>
        <p>This deal is not considered won until the money is in the IOLTA.</p>
    </div>,

    d_waitingOn: 'Waiting On',
    h_waitingOn: <div>
        <p>Deals can be moved to the Parking Lot when the client wants to hire your firm, but the time is not right.</p>
        <p>For example, they may need time to put together the funds, or a mandatory waiting period is in effect.</p>
    </div>,

    d_retainerFunded: 'Retainer Funded (Won)',
    h_retainerFunded: <div>
        <p>The signed the retainer agreement and deposited funds into the IOLTA account.</p>
        <p>The deal is now considered won.</p>
    </div>,

    d_consultSufficient: 'Consult Sufficient (Won)',
    h_consultSufficient: <div>
        <p>The PNC received enough information during the Initial Consult that they do not feel they need ongoing legal
            representation.
        </p>
        <p>For example, if they decide (based on legal advice) that they do not want to follow through with legal
            action.
        </p>
    </div>,

    d_lost: 'Lost',
    h_lost: <div>
        <p>A PNC that your firm would like to retain has decided not to retain your services because they decided to go
            elsewhere.</p>
        <p>In other words, you wanted them but they did not want you.</p>
    </div>,

    d_refused: 'Refused',
    h_refused: <div>
        <p>You decided that the PNC was not a good fit for your firm, for whatever reason.</p>
        <p>In other words, they may have wanted you, but you do not want them.</p>
    </div>,

    d_name: 'Short Name',
    h_name: '<div><p>Give a short name for the lead.</p><p>This will be included on invoices for initial consults (if you are using paid consults).</p><p>Client names are entered in the next field, <b>Client Names</b></p></div>',

    d_referrers: 'Referred By',
    h_referrers: `<div><p>List any person or company that influenced the potential new client(s) to contact you.</p><p style="color: maroon"><b>This must be a person or a business!</b></p><p>If you enter freeform text here, like "A Friend", Outlaw will assume that you meant a person with the name "Friend, A.", and it will add a new contact to the system with that name. This is clearly not what you intended!</p><p>If you encounter a lead that was referred by an anonymous or unknown person, just leave this field blank.</p><b>When to Use Referred By vs Campaigns</b><p>Use <b>Referred By</b> when the referral came from an actual person or business.</p><p><b>Campaigns</b> can be used with, or instead of <b>Referred By</b>.</p><p>Examples of Campaigns with no Referred By: <ul><li>Google or Facebook Ads</li><li>A lead generation company</li><li>A directory, e.g. AVVO or Justia</li></ul></p><p>Examples of Campaigns with a Referred By: <ul><li>Another law firm. Referred By would be the name of the person at the firm that did the referral, or the name of the firm if unknown. The Campaign would be something like "ABC Law Firm". Make sure you have a campaign for the law firm doing the referrals!</li><li>A networking group, like BNI or a Chamber of Commerce. The Referred By field would be the name of the person within the group that made the actual referral.</li><li>A Former Client. Use the name of the client in the Referred By field, and have a campaign called Former Clients.</li></ul></p><p>The point of having these separate is to provide proper data for analytics, so that the firm owner knows where their referrals are coming from (so that they can thank them), and which marketing campaigns are producing good leads (so they can invest in them if they are good, or divest from them if they are bad).</p></div>`,

    d_caseTypes: 'Case Types',
    h_caseTypes: <div>
        <p>Select the case types that apply to this lead.</p>
        <p>If a potential new client has multiple issues, you can list all of the related case types here,
            or you can choose to create a new lead for each one.</p>
        <p>
            <RoutedIconButton
                id='link-to-case-types'
                type={Icons.CASE}
                color='navy'
                label='Go to Case Types'
                route='/config/leads/case-types'
            />
        </p>
    </div>,

    d_clientNames: 'Client Names',
    h_clientNames: <div>
        <p>The names of the potential new clients.</p>
        <p>Note that a single lead can lead to multiple cases. It is your choice to create one lead with multiple
            potential cases, or a separate lead per potential case. Outlaw Practice will work the same regardless of
            your choice.</p>
        <p>You can either select existing contacts, or you can enter new ones directly by typing in the full name of
            the contact.</p>
        <p>Once you have saved this Lead, you will be able to edit their details further in the Contact tab.</p>
        <p>
            <RoutedIconButton id='link-to-contacts'
                        type={Icons.CONTACT}
                        title='Go to Contacts'
                        color='navy'
                        label='Go to Contacts'
                        route='/contacts'
            />
        </p>
    </div>,

    d_campaigns: 'Campaigns',
    h_campaigns: <div>
        <p>Which campaign(s) influenced the potential new client to reach out to you?</p>
        <p>You can choose campaigns in addition to a referrer (see Referred By help for more info).</p>
        <p>We keep track of campaigns so that the firm owner knows whether to invest (if the campaign is bringing
            good leads that pay their bills), or divest (if the campaign is bringing in the wrong type of client, or the
            campaign is bringing in clients that are not paying their bills.</p>
        <p>Additional campaigns can be set up by clicking the
            <FontAwesomeIcon icon={Icons.HANDSHAKE.reactIcon}/></p>
        <p>
            <RoutedIconButton id='link-to-campaigns'
                        type={Icons.HANDSHAKE}
                        title='Go to Marketing'
                        color='navy'
                        label='Campaign Management'
                        route='/marketing/campaigns'
            />
        </p>
    </div>,

    d_estimatedClose: 'Estimated Close',
    h_estimatedClose: '<div><p>This is your best guess as to when the potential new client will sign the retainer agreement.</p><p>This helps you understand when your pipeline is expected to convert to sales (client signing the retainer agreement and funding their retainer).</p></div>',

    d_leadCost: 'Lead Cost',
    h_leadCost: <div>
        <p>What this lead cost you, averaged across the campaigns it came from.</p>
        <p>Each campaign carries a cost per lead, set on the campaign itself. Compare this against the
            estimated value to see whether a campaign is paying for itself.</p>
    </div>,

    d_consultActions: 'Consult',
    h_consultActions: <div>
        <p>What you can do next for the initial consult, once an appointment has been made.</p>
        <p>Depending on where the lead stands, this is where you take payment for a paid consult,
            add the time entry for the consult itself, or turn completed time into an invoice.</p>
    </div>,

    d_milestones: 'Milestones',
    h_milestones: <div>
        <p>Where this lead stands in your sales pipeline.</p>
        <p>Milestones are recorded in order, and each one records something that has already happened:
            click a milestone once its description is true. Click a completed milestone to return to it,
            which undoes it and everything after.</p>
        <p>The resolution ends the lead: won by a funded retainer or a sufficient consult, refused, lost,
            or parked while the potential new client decides.</p>
    </div>,

    d_estimatedValue: 'Estimated Value',
    h_estimatedValue: <div>
        <p>The estimated <b>total value</b> of cases resulting from this lead.</p>
        <p>The estimated value is initially based on the case type(s), as set in their <b>Average Price</b>,
            but updated based on your conversations with the potential new client(s).</p>
        <p>If this looks like a difficult case, raise the estimated value. If it looks like a simple case,
            lower the value.</p>
        <p>
            <RoutedIconButton id='link-to-case-types'
                        type={Icons.CASE}
                        title='Go to Case Types'
                        color='navy'
                        label='Go to Case Types'
                        route='/config/leads/case-types'
            />
        </p>
    </div>,

    d_initialConsultDate: 'Initial Consult Date',
    h_initialConsultDate: <div>
        <p>Once an initial consultation has been set, put the date here.</p>
        <p>If the case type(s) are configured to use paid consults, the <b>Paid Consult</b> checkbox will be checked.
        </p>
        <p>If not, you have the option of overriding the case type configuration on a one-off basis by checking
            the <b>Paid Consult</b> checkbox here.</p>
        <p>
            <RoutedIconButton id='link-to-paid-consults-2'
                        type={Icons.CONFIG}
                        title='Go to Configuration'
                        color='navy'
                        label='Go to Configuration'
                        route='/config/leads/settings'
            />
        </p>
        <p>
            <RoutedIconButton id='link-to-case-types'
                        type={Icons.CASE}
                        title='Go to Case Types'
                        color='navy'
                        label='Go to Case Types'
                        route='/options/caseTypes'
            />
        </p>
    </div>,

    d_payer: 'Who is Paying?',
    h_payer: `<div><p>If one of the clients is paying, this field should be empty.</p><p>If there is a benefactor outside of the clients, then put that person's name here.</p><p>More than one beneficiary can be listed here, and any beneficiaries will be added to cases created from this lead.</p><p>When taking a payment, beneficiaries will be listed first in the payor dropdown with a special icon.</p></div>`,

    d_retainerRequested: 'Retainer Requested',
    h_retainerRequested: <div>
        <p>How much do you require to get started on this case?</p>
        <p>This value will be based on the <b>Retainer</b> field from the case type configuration, but it can be
            modified based on your assessment of the case during the sales process.</p>
        <p>
            <RoutedIconButton id='link-to-case-types'
                        type={Icons.CONFIG}
                        title='Go to Configuration'
                        color='navy'
                        label='Go to Configuration'
                        route='/config/leads/settings'
            />
        </p>
    </div>,

    d_retainerReceived: 'Retainer Received',
    h_retainerReceived: '<div><p>How much of the requested retainer was the client able to provide?</p><p>This is a reminder that starting work before the full retainer is provided is heavily associated with non-payment later in the case.</p></div>',

    h_createCaseForLead: (params: {caseTypeNames: string[]}) => <div>
        <p><b>Shall we create a new case linked to this lead?</b></p>
        <p>This will create a new case of type <em>{params.caseTypeNames[0]}</em>,
            link it to this lead, and copy Intake fields where applicable.
        </p>
    </div>,

    h_createCasesForLead: (params: {numCases: number}) => <div>
        <p><b>Shall we create new cases linked to this lead?</b></p>
        <p>This will create {params.numCases} cases, one for each case type.</p>
    </div>,

    d_errorSaveContact: 'Could not call save on contact reference',
};